import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DesktopShell } from '../components/DesktopShell';
import { TopBar } from '../components/TopBar';
import { Card } from '../components/Card';
import { Btn } from '../components/Btn';
import { Pill } from '../components/Pill';
import { Flag } from '../components/Flag';
import { Ic } from '../components/Icon';
import { Modal } from '../components/Modal';
import { AdminTabs } from '../components/AdminTabs';
import { IconAction } from '../components/IconAction';
import { matchesApi } from '../api/matches';
import { teamNamePl } from '../lib/flags';
import { phaseLabel } from '../lib/phase';
import { formatTime, formatDateOnly } from '../lib/date';
import { pickDisplayScore } from '../lib/score';
import type { Match } from '../api/types';

export function AdminMatchesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const matches = useQuery({ queryKey: ['matches'], queryFn: () => matchesApi.list() });
  const [toDelete, setToDelete] = useState<Match | null>(null);

  const del = useMutation({
    mutationFn: (id: string) => matchesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matches'] });
      setToDelete(null);
    },
  });

  const stats = (() => {
    const list = matches.data ?? [];
    return {
      total: list.length,
      finished: list.filter((m) => m.status === 'FINISHED').length,
      live: list.filter((m) => m.status === 'LIVE').length,
      upcoming: list.filter((m) => m.status === 'UPCOMING').length,
    };
  })();

  return (
    <DesktopShell>
      <TopBar
        title="Panel admina"
        subtitle="Zarządzanie meczami turnieju"
      />
      <AdminTabs />
      <div className="responsive-pad" style={{ flex: 1, padding: '0 36px 40px' }}>
        <div className="admin-kpi" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
          <Stat label="Wszystkie" value={stats.total} />
          <Stat label="Zagrane" value={stats.finished} />
          <Stat label="Na żywo" value={stats.live} />
          <Stat label="Planowane" value={stats.upcoming} />
        </div>

        {matches.isLoading && <Card padding={20}>Ładowanie…</Card>}

        {matches.data && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              marginBottom: 12,
              flexWrap: 'wrap',
            }}
          >
            <h3 className="serif" style={{ margin: 0, fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em' }}>
              Wszystkie mecze{' '}
              <span style={{ color: '#5b6578', fontWeight: 500 }}>· {matches.data.length}</span>
            </h3>
            <Btn
              variant="primary"
              size="md"
              icon={<Ic.plus size={14} />}
              onClick={() => navigate('/admin/matches/new')}
            >
              Nowy mecz
            </Btn>
          </div>
        )}

        {matches.data && (
          <Card padding={0} className="desktop-only">
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '90px 80px 1.4fr 90px 1.4fr 110px 110px 90px',
                padding: '14px 20px',
                fontSize: 10.5,
                color: '#5b6578',
                fontWeight: 600,
                letterSpacing: '.06em',
                borderBottom: '1px solid rgba(15,30,61,.08)',
              }}
            >
              <div>DATA</div>
              <div>GODZ</div>
              <div style={{ textAlign: 'right' }}>GOSPODARZE</div>
              <div style={{ textAlign: 'center' }}>WYNIK</div>
              <div>GOŚCIE</div>
              <div>FAZA</div>
              <div>STATUS</div>
              <div></div>
            </div>
            {matches.data.map((m, i) => (
              <div
                key={m.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '90px 80px 1.4fr 90px 1.4fr 110px 110px 90px',
                  padding: '11px 20px',
                  fontSize: 13,
                  alignItems: 'center',
                  borderBottom: i < matches.data!.length - 1 ? '1px solid rgba(15,30,61,.05)' : 'none',
                  background: i % 2 === 0 ? 'transparent' : 'rgba(15,30,61,.015)',
                }}
              >
                <div className="num" style={{ color: '#5b6578' }}>{formatDateOnly(m.kickoff)}</div>
                <div className="num" style={{ fontWeight: 600 }}>{formatTime(m.kickoff)}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                  <span>{m.homePlaceholder ?? teamNamePl(m.homeTeam)}</span>
                  <Flag code={m.homePlaceholder ?? m.homeTeam} size={20} />
                </div>
                <div className="num serif" style={{ textAlign: 'center', fontWeight: 700 }}>
                  {(() => {
                    const ds = pickDisplayScore(m);
                    if (!ds) return <span style={{ color: '#c5cad4' }}>—</span>;
                    return (
                      <span title={ds.annotationVerbose ?? undefined}>
                        {ds.mainHome}–{ds.mainAway}
                        {ds.annotationShort && (
                          <span style={{ fontSize: 11, fontWeight: 500, color: '#5b6578', marginLeft: 6 }}>
                            ({ds.annotationShort})
                          </span>
                        )}
                      </span>
                    );
                  })()}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Flag code={m.awayPlaceholder ?? m.awayTeam} size={20} />
                  <span>{m.awayPlaceholder ?? teamNamePl(m.awayTeam)}</span>
                </div>
                <div style={{ fontSize: 11.5, color: '#5b6578' }}>{phaseLabel(m.phase, m.group)}</div>
                <div>
                  {m.status === 'FINISHED' && <Pill color="green"><Ic.check size={9} />Zakończony</Pill>}
                  {m.status === 'LIVE' && <Pill color="live">LIVE</Pill>}
                  {m.status === 'UPCOMING' && <Pill color="gray"><Ic.clock size={9} />Planowany</Pill>}
                  {m.status === 'PENDING_DRAW' && <Pill color="gray"><Ic.clock size={9} />Oczekuje</Pill>}
                </div>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  <Link to={`/admin/matches/${m.id}`} aria-label="Edytuj mecz">
                    <IconAction icon={<Ic.edit size={16} />} label="Edytuj mecz" />
                  </Link>
                  <IconAction
                    icon={<Ic.trash size={16} />}
                    label="Usuń mecz"
                    danger
                    onClick={() => setToDelete(m)}
                  />
                </div>
              </div>
            ))}
          </Card>
        )}

        {matches.data && (
          <div className="mobile-only" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {matches.data.map((m) => (
              <Card key={m.id} padding={12}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                  <div style={{ fontSize: 11.5, color: '#5b6578', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span className="num" style={{ fontWeight: 600, color: '#0f1e3d' }}>{formatDateOnly(m.kickoff)}</span>
                    <span>·</span>
                    <span className="num">{formatTime(m.kickoff)}</span>
                    <span>·</span>
                    <span>{phaseLabel(m.phase, m.group)}</span>
                  </div>
                  <div style={{ flexShrink: 0 }}>
                    {m.status === 'FINISHED' && <Pill color="green"><Ic.check size={9} />Zakończony</Pill>}
                    {m.status === 'LIVE' && <Pill color="live">LIVE</Pill>}
                    {m.status === 'UPCOMING' && <Pill color="gray"><Ic.clock size={9} />Planowany</Pill>}
                  </div>
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 10,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end', minWidth: 0 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {m.homePlaceholder ?? teamNamePl(m.homeTeam)}
                    </span>
                    <Flag code={m.homePlaceholder ?? m.homeTeam} size={20} />
                  </div>
                  <div className="num serif" style={{ fontSize: 16, fontWeight: 700, textAlign: 'center', minWidth: 50 }}>
                    {(() => {
                      const ds = pickDisplayScore(m);
                      if (!ds) return <span style={{ color: '#c5cad4' }}>—</span>;
                      return (
                        <div title={ds.annotationVerbose ?? undefined} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <span>{ds.mainHome}–{ds.mainAway}</span>
                          {ds.annotationShortLines.map((line, i) => (
                            <span key={i} style={{ fontSize: 10, fontWeight: 500, color: '#5b6578', whiteSpace: 'nowrap', marginTop: 1 }}>
                              {line}
                            </span>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                    <Flag code={m.awayPlaceholder ?? m.awayTeam} size={20} />
                    <span style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {m.awayPlaceholder ?? teamNamePl(m.awayTeam)}
                    </span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <Link to={`/admin/matches/${m.id}`} aria-label="Edytuj mecz">
                    <IconAction icon={<Ic.edit size={16} />} label="Edytuj mecz" />
                  </Link>
                  <IconAction
                    icon={<Ic.trash size={16} />}
                    label="Usuń mecz"
                    danger
                    onClick={() => setToDelete(m)}
                  />
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Modal
        open={!!toDelete}
        onClose={() => !del.isPending && setToDelete(null)}
        title="Usunąć mecz?"
        footer={
          <>
            <Btn variant="ghost" size="md" onClick={() => setToDelete(null)} disabled={del.isPending}>
              Anuluj
            </Btn>
            <Btn
              variant="danger"
              size="md"
              icon={<Ic.trash size={14} />}
              onClick={() => toDelete && del.mutate(toDelete.id)}
              disabled={del.isPending}
            >
              {del.isPending ? 'Usuwanie…' : 'Usuń'}
            </Btn>
          </>
        }
      >
        {toDelete && (
          <>
            <p style={{ margin: '0 0 8px' }}>
              Mecz <strong>{toDelete.homePlaceholder ?? teamNamePl(toDelete.homeTeam)} – {toDelete.awayPlaceholder ?? teamNamePl(toDelete.awayTeam)}</strong>{' '}
              ({formatDateOnly(toDelete.kickoff)} · {formatTime(toDelete.kickoff)}) zostanie trwale usunięty.
            </p>
            <p style={{ margin: 0, color: '#5b6578', fontSize: 13 }}>
              Tej akcji nie da się cofnąć — typy graczy na ten mecz przepadną razem z nim.
            </p>
          </>
        )}
      </Modal>
    </DesktopShell>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <Card padding={16}>
      <div style={{ fontSize: 11, color: '#5b6578', fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase' }}>
        {label}
      </div>
      <div className="serif num" style={{ fontSize: 28, fontWeight: 700, marginTop: 4 }}>
        {value}
      </div>
    </Card>
  );
}

